// React
import { useEffect, useState } from "react";

// Third-party
import { Box, Collapse, IconButton, Stack, Typography } from "@mui/material";
import {
  CheckCircle2,
  ChevronRight,
  ChevronUp,
  Loader2,
  XCircle,
} from "lucide-react";

// Internal
import { AnimatedDots } from "./LoadingBlock";
import {
  getThinkingBlockContainerStyle,
  getThinkingBlockTitleStyle,
  getThinkingContentTextStyle,
  getThinkingStepActiveDotStyle,
  getThinkingStepActiveLabelStyle,
  getThinkingStepLabelStyle,
  getToolStatusLabelStyle,
  getToolSummaryTextStyle,
  spinningLoaderIconStyle,
  thinkingBlockBodyStyle,
  thinkingBlockHeaderStyle,
  thinkingStepInactiveDotStyle,
  toolBlockContainerStyle,
  toolBlockHeaderStyle,
} from "@pages/askDost/styles/askDostStyles";
import { TOOL_STATUS } from "@pages/askDost/utils/constants";

// Types
import {
  IThinkingBlockProps,
  IThinkingStep,
  IToolBlockProps,
} from "@pages/askDost/types";

const ToolBlock = ({
  block,
  palette,
  translate,
  isStreaming,
}: IToolBlockProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const rawStatus = block.toolStatus || TOOL_STATUS.PENDING;
  const status =
    !isStreaming &&
    (rawStatus === TOOL_STATUS.PENDING || rawStatus === TOOL_STATUS.IN_PROGRESS)
      ? TOOL_STATUS.DONE
      : rawStatus;

  const isActive =
    status === TOOL_STATUS.PENDING || status === TOOL_STATUS.IN_PROGRESS;

  const spinningLoader = (
    <Box component={Loader2} size={16} sx={spinningLoaderIconStyle} />
  );

  const statusIcon = {
    [TOOL_STATUS.PENDING]: spinningLoader,
    [TOOL_STATUS.IN_PROGRESS]: spinningLoader,
    [TOOL_STATUS.DONE]: <CheckCircle2 size={16} color={palette.success.dark} />,
    [TOOL_STATUS.ERROR]: <XCircle size={16} color={palette.error.dark} />,
  }[status];

  const statusLabel =
    status === TOOL_STATUS.DONE
      ? translate("tool_completed", { tool: translate(block.toolName!) })
      : status === "error"
      ? translate("tool_failed", { tool: translate(block.toolName!) })
      : translate("running_tool", { tool: translate(block.toolName!) });

  return (
    <Box sx={toolBlockContainerStyle}>
      <Box sx={toolBlockHeaderStyle} onClick={() => setIsExpanded(!isExpanded)}>
        <Box sx={{ mb: 1 }}>{statusIcon}</Box>

        <Typography
          variant="body2"
          sx={getToolStatusLabelStyle(palette, isActive)}
        >
          {statusLabel}
          {isActive && <AnimatedDots palette={palette} />}
        </Typography>
        <IconButton size="small" sx={{ p: 0 }}>
          {isExpanded ? <ChevronUp size={16} /> : <ChevronRight size={16} />}
        </IconButton>
      </Box>
      <Collapse in={isExpanded} timeout={200}>
        <Typography variant="caption" sx={getToolSummaryTextStyle(palette)}>
          {block.toolSummary ||
            (block.toolInput ? JSON.stringify(block.toolInput) : "")}
        </Typography>
      </Collapse>
    </Box>
  );
};

const ThinkingStepsChecklist = ({
  steps,
  palette,
}: {
  steps: IThinkingStep[];
  palette: IThinkingBlockProps["palette"];
}) => {
  const activeIndex = steps?.findIndex((step) => !step.done);

  return (
    <Stack spacing={1} sx={{ mt: 1 }}>
      {steps?.map((step, index) => {
        const isActive = index === activeIndex;
        return (
          <Stack
            key={`${step.label}-${index}`}
            direction="row"
            spacing={1}
            alignItems="flex-start"
          >
            {step.done ? (
              <CheckCircle2
                size={16}
                color={palette.success.dark}
                style={{ marginTop: 2, flexShrink: 0 }}
              />
            ) : isActive ? (
              <Box sx={getThinkingStepActiveDotStyle(palette)} />
            ) : (
              <Box sx={thinkingStepInactiveDotStyle} />
            )}
            {isActive ? (
              <Typography
                variant="body2"
                sx={getThinkingStepActiveLabelStyle(palette)}
              >
                {step.label}
              </Typography>
            ) : (
              <Typography
                variant="body2"
                sx={getThinkingStepLabelStyle(palette, step.done)}
              >
                {step.label}
              </Typography>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
};

export const ThinkingBlock: React.FC<IThinkingBlockProps> = ({
  block,
  palette,
  translate,
  isStreaming,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);

  const { content, thinkingSteps } = block;

  const toolsToRender =
    (block as any).groupedTools || (block.toolName ? [block] : []);
  const hasTools = toolsToRender.length > 0;
  const hasExplicitSteps = !!thinkingSteps?.length;

  const implicitSteps: IThinkingStep[] = hasExplicitSteps
    ? []
    : [
        {
          label: translate("understanding_your_query"),
          done: Boolean(content) || hasTools || !isStreaming,
        },
      ];

  const rawSteps = hasExplicitSteps ? thinkingSteps! : implicitSteps;

  const stepsToRender = rawSteps.map((step) => ({
    ...step,
    done: step.done || !isStreaming,
  }));

  useEffect(() => {
    if (!isStreaming) {
      setIsExpanded(false);
    } else {
      setIsExpanded(true);
    }
  }, [isStreaming]);

  return (
    <Box sx={getThinkingBlockContainerStyle(palette)}>
      <Box
        sx={thinkingBlockHeaderStyle}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Typography variant="body2" sx={getThinkingBlockTitleStyle(palette)}>
          {isStreaming ? translate("thinking") : translate("thought_process")}
          {isStreaming && <AnimatedDots palette={palette} />}
        </Typography>
        <IconButton size="small" sx={{ p: 0 }}>
          {isExpanded ? <ChevronUp size={16} /> : <ChevronRight size={16} />}
        </IconButton>
      </Box>
      <Collapse in={isExpanded} timeout={200}>
        <Box sx={thinkingBlockBodyStyle}>
          {content && (
            <Typography
              variant="body2"
              sx={getThinkingContentTextStyle(palette)}
            >
              {content}
            </Typography>
          )}

          <ThinkingStepsChecklist steps={stepsToRender} palette={palette} />

          {toolsToRender?.map((tBlock: any, index: number) => (
            <ToolBlock
              key={index}
              block={tBlock}
              palette={palette}
              translate={translate}
              isStreaming={isStreaming}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
};
